import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, Eye, EyeOff, ExternalLink, Copy, Check, Star, CalendarCheck, Sparkles, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { UpgradeCard } from "@/components/UpgradeCard";

const TYPE_META = {
  review: { label: "New reviews", icon: Star, help: "Real synced reviews of 4 stars or more that passed the content check." },
  booking: { label: "Recent bookings", icon: CalendarCheck, help: "Jobs you actually booked or won. Never typed in by hand." },
  aggregate: { label: "Review score", icon: Sparkles, help: "Live count and average from the connected profiles." },
  custom: { label: "Your own message", icon: MessageSquare, help: "One line you write yourself, checked before it goes live." },
};

export function ProofTab({ site }) {
  const siteId = site.id;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}/proof`);
      setData(data);
      setMsg(data.config?.custom_message || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load pop-ups");
    }
  };
  useEffect(() => { load(); }, [siteId]);

  const save = async (patch, what = "save") => {
    setBusy(what);
    try {
      const { data } = await api.patch(`/sites/${siteId}/proof`, patch);
      setData(data);
      setMsg(data.config?.custom_message || "");
      toast.success("Saved.");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "content_blocked") toast.error((d.reasons || [])[0] || d.message);
      else toast.error(formatApiError(d) || "Could not save");
    } finally {
      setBusy("");
    }
  };

  const toggleHidden = async (ev) => {
    setBusy(ev.id);
    try {
      if (ev.hidden) await api.delete(`/sites/${siteId}/proof/hide/${encodeURIComponent(ev.id)}`);
      else await api.post(`/sites/${siteId}/proof/hide/${encodeURIComponent(ev.id)}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  if (!data.entitled) {
    return (
      <div className="mx-auto max-w-lg" data-testid="proof-upgrade">
        <UpgradeCard siteId={siteId} offer={data.upgrade} />
      </div>
    );
  }

  const cfg = data.config;
  const rules = cfg.rules;
  const click = cfg.click || { mode: "smart", booking_url: "", call_number: "", all_url: "" };
  const visible = data.events.filter((e) => !e.hidden);
  // the server decides the public host (REPUTATION_PUBLIC_BASE, else APP_ORIGIN, never loopback)
  const tag = data.embed_base
    ? `<script async src="${data.embed_base}/api/embed/beacon.js" data-lp-beacon="${data.form_token}"></script>`
    : "Embed host not set on the server. Set REPUTATION_PUBLIC_BASE (or APP_ORIGIN), then reload.";

  return (
    <div className="space-y-6" data-testid="proof-tab">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-border bg-card p-5">
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-primary" />
            <p className="font-display text-lg font-semibold tracking-tight">Activity pop-ups</p>
            <Badge variant={cfg.enabled ? "default" : "outline"} data-testid="proof-state">
              {cfg.enabled ? "Live on the site" : "Off"}
            </Badge>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Small, honest notices in the corner of the client's website: real reviews, real bookings, real numbers.
            If there is nothing real to show, nothing shows. {visible.length} event{visible.length === 1 ? "" : "s"} would
            run right now.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!cfg.enabled} data-testid="proof-enabled"
            onCheckedChange={(v) => save({ enabled: v }, "enabled")} />
          <span className="text-sm">{cfg.enabled ? "On" : "Off"}</span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm font-semibold">What can pop up</p>
          <div className="mt-3 space-y-3">
            {Object.entries(TYPE_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <div key={key} className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{meta.label}</p>
                      <p className="text-xs text-muted-foreground">{meta.help}</p>
                    </div>
                  </div>
                  <Switch checked={!!cfg.types[key]} data-testid={`proof-type-${key}`}
                    onCheckedChange={(v) => save({ types: { [key]: v } }, key)} />
                </div>
              );
            })}
          </div>

          <div className="mt-4 space-y-1 border-t border-border pt-4">
            <Label htmlFor="proofMsg">Your own message</Label>
            <Textarea id="proofMsg" rows={2} value={msg} maxLength={160} data-testid="proof-custom-message"
              placeholder="Now booking spring gutter cleanings." onChange={(e) => setMsg(e.target.value)} />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">No discounts or incentives. Checked before it goes live.</p>
              <Button size="sm" variant="outline" disabled={busy === "msg"} data-testid="proof-save-message"
                onClick={() => save({ custom_message: msg }, "msg")}>Save message</Button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm font-semibold">How often</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {[
              ["first_delay_s", "First pop-up after (s)"],
              ["min_gap_s", "Then every, at least (s)"],
              ["max_gap_s", "…and at most (s)"],
              ["max_per_visit", "Max per visit"],
              ["show_ms", "Each shown for (ms)"],
            ].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label htmlFor={k} className="text-xs">{label}</Label>
                <Input id={k} type="number" min={1} defaultValue={rules[k]} data-testid={`proof-rule-${k}`}
                  onBlur={(e) => Number(e.target.value) !== rules[k] && save({ rules: { [k]: e.target.value } }, k)} />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs">Corner</Label>
              <select value={rules.position} data-testid="proof-rule-position"
                onChange={(e) => save({ rules: { position: e.target.value } }, "pos")}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="bottom-left">Bottom left</option>
                <option value="bottom-right">Bottom right</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-start justify-between gap-3 border-t border-border pt-4">
            <div>
              <p className="text-sm font-medium">{data.verified_mark.text}</p>
              <p className="text-xs text-muted-foreground">
                {data.verified_mark.can_disable
                  ? "You can switch the mark off on Complete."
                  : "The mark stays on at this tier. Complete can switch it off."}
              </p>
            </div>
            <Switch checked={!!cfg.verified_mark} disabled={!data.verified_mark.can_disable}
              data-testid="proof-verified-mark"
              onCheckedChange={(v) => save({ verified_mark: v }, "mark")} />
          </div>

          <div className="mt-4 border-t border-border pt-4">
            <p className="text-sm font-semibold">Where a click goes</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Smart: review cards open the business's Google reviews listing so a prospect can read them;
              booking, score and your own message open the booking page — and tap-to-call on phones.
            </p>
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap gap-2">
                {[["smart", "Smart (per card)"], ["all", "One destination for all cards"]].map(([m, label]) => (
                  <Button key={m} size="sm" variant={click.mode === m ? "default" : "outline"}
                    data-testid={`proof-click-mode-${m}`}
                    onClick={() => save({ click: { mode: m } }, "mode")}>{label}</Button>
                ))}
              </div>
              {click.mode === "smart" ? (
                <div className="space-y-1">
                  <Label htmlFor="bookingUrl" className="text-xs">Booking or contact page</Label>
                  <Input id="bookingUrl" defaultValue={click.booking_url} data-testid="proof-booking-url"
                    placeholder="https://customgutters.pro/book"
                    onBlur={(e) => e.target.value !== click.booking_url && save({ click: { booking_url: e.target.value } }, "burl")} />
                </div>
              ) : (
                <div className="space-y-1">
                  <Label htmlFor="allUrl" className="text-xs">Send every card here</Label>
                  <Input id="allUrl" defaultValue={click.all_url} data-testid="proof-all-url"
                    placeholder="https://customgutters.pro/book"
                    onBlur={(e) => e.target.value !== click.all_url && save({ click: { all_url: e.target.value } }, "aurl")} />
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="callNumber" className="text-xs">Pop-up call number (optional)</Label>
                <Input id="callNumber" defaultValue={click.call_number} data-testid="proof-call-number"
                  placeholder="(352) 555-0134"
                  onBlur={(e) => e.target.value !== click.call_number && save({ click: { call_number: e.target.value } }, "call")} />
                <p className="text-xs text-muted-foreground">
                  Used for tap-to-call on phones. Leave blank to use the verified number on the account.
                </p>
              </div>
              {!data.click_ready && (
                <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400"
                  data-testid="proof-no-destination">
                  No click destination set — the cards will show but won't be clickable until you add one.
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 border-t border-border pt-4">
            <p className="text-sm font-semibold">One tag for every Beacon widget</p>
            <code className="mt-2 block break-all rounded-md bg-muted px-3 py-2 text-xs" data-testid="proof-embed-tag">{tag}</code>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" data-testid="proof-copy-tag" disabled={!data.embed_base}
                onClick={() => { navigator.clipboard.writeText(tag); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
                {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />} Copy tag
              </Button>
              <Button size="sm" variant="ghost" asChild data-testid="proof-preview">
                <a href={`${base}/api/embed/proof/${data.form_token}/preview`} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Preview
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold">What would show, in order</p>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground" data-testid="proof-stats">
            <Badge variant="outline">{data.stats.impressions || 0} shown</Badge>
            <Badge variant="outline">{data.stats.clicks || 0} clicks</Badge>
            <Badge variant="outline" data-testid="proof-stat-calls">{data.stats.calls || 0} calls</Badge>
          </div>
        </div>
        {Object.keys(data.stats.by_type || {}).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground" data-testid="proof-stats-by-type">
            {Object.entries(data.stats.by_type).map(([t, v]) => (
              <span key={t} className="rounded-md border border-border px-2 py-1">
                {TYPE_META[t]?.label || t}: {v.impressions} shown · {v.clicks} clicks · {v.calls} calls
              </span>
            ))}
          </div>
        )}

        {!data.events.length ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="proof-no-events">
            Nothing real to show yet. Once reviews sync or a lead is marked booked, events appear here —
            Beacon never invents one.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-border" data-testid="proof-event-list">
            {data.events.map((ev) => (
              <li key={ev.id} className="flex items-start justify-between gap-4 py-3" data-testid={`proof-event-${ev.type}`}>
                <div className={ev.hidden ? "opacity-50" : ""}>
                  <p className="text-sm">
                    <span className="font-medium">{ev.name || site.business_name || site.url}</span>
                    {ev.town ? <span className="text-muted-foreground"> · {ev.town}</span> : null}
                    {ev.rating ? <span className="ml-2 text-amber-500">{"★".repeat(Math.round(ev.rating))}</span> : null}
                  </p>
                  <p className="text-sm text-muted-foreground">{ev.text}</p>
                  <p className="text-xs text-muted-foreground">
                    {TYPE_META[ev.type]?.label}{ev.age ? ` · ${ev.age}` : ""}{ev.source ? ` · ${ev.source}` : ""}
                  </p>
                </div>
                <Button size="sm" variant="ghost" disabled={busy === ev.id} data-testid={`proof-hide-${ev.id}`}
                  onClick={() => toggleHidden(ev)}>
                  {ev.hidden ? <><Eye className="mr-1.5 h-3.5 w-3.5" /> Show</> : <><EyeOff className="mr-1.5 h-3.5 w-3.5" /> Hide</>}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
